/**
 * Temples and their signed agreements — `/v1/admin/temples*`, `/v1/admin/authorizations*`.
 *
 * **The official-wording flags are read-only, everywhere, forever.** `authorization.official_puja`
 * is derived by the server from the agreements on every read; there is no endpoint that sets it
 * and this module offers no mutation that pretends there is. That is product rule 6 made
 * structural: the claim dies when the paper expires, without anybody remembering to untick a box.
 *
 * **Publishing is guarded and the server names the blockers.** `publish_blockers` comes back as a
 * list of missing things — a hero image, a deity, a timing row, prose — so the screen can show
 * four checkboxes instead of one refusal an editor has to decode.
 *
 * **A renewal is a new agreement.** Nothing leaves `expired` or `terminated`; the create route is
 * also the renewal route, and the gap between two agreements stays visible in the history.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import { putPresigned } from '../storage';
import type { components } from '../../../api/generated/schema';

export type TempleRow = components['schemas']['TempleRow'];
export type TempleDetail = components['schemas']['AdminTempleDetail'];
export type AuthorizationRow = components['schemas']['AuthorizationRow'];
export type OfficialFlags = components['schemas']['OfficialFlags'];
export type TempleList = components['schemas']['AdminTempleListResponse'];
type AuthorizationList = components['schemas']['AuthorizationListResponse'];
type ExpiringList = components['schemas']['ExpiringListResponse'];

export interface TempleQuery extends Record<string, unknown> {
  page: number;
  page_size: number;
  status?: string;
  include_deleted?: boolean;
}

/**
 * Exported for its own test: an absent filter and an explicit one are different requests.
 *
 * Only the filters the screen actually uses. The endpoint also accepts `city_id`; adding it here
 * before a control sends it would be a parameter nobody can reach, sitting inside a 100% gate
 * proving only that it compiles. `include_deleted` is sent by the Archived tab: archiving
 * soft-deletes the temple, so without it that tab is always empty.
 */
export function templeQueryString(params: TempleQuery): string {
  const search = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.page_size),
  });
  if (params.status !== undefined) search.set('status', params.status);
  if (params.include_deleted) search.set('include_deleted', 'true');
  return search.toString();
}

export function useTemples(params: TempleQuery) {
  return useQuery({
    queryKey: keys.temples.list(params),
    queryFn: () => authedClient.request<TempleList>(`/v1/admin/temples?${templeQueryString(params)}`),
  });
}

export function useTemple(id: number | undefined) {
  return useQuery({
    queryKey: keys.temples.one(id ?? 0),
    queryFn: () => authedClient.request<TempleDetail>(`/v1/admin/temples/${id}`),
    enabled: id !== undefined,
  });
}

export function useAuthorizations(templeId: number | undefined) {
  return useQuery({
    queryKey: keys.temples.authorizations(templeId ?? 0),
    queryFn: () =>
      authedClient.request<AuthorizationList>(`/v1/admin/temples/${templeId}/authorizations`),
    enabled: templeId !== undefined,
  });
}

/** The window is the server's `temples.expiring_queue_window_days`, so ops tune it in Settings. */
export function useExpiringAuthorizations() {
  return useQuery({
    queryKey: keys.temples.expiring(),
    queryFn: () => authedClient.request<ExpiringList>('/v1/admin/authorizations/expiring'),
  });
}

/**
 * Every temple write invalidates every temple read.
 *
 * Coarse on purpose: activating an agreement changes the authorization list, the temple's derived
 * flags and the renewal board at once, and a hand-maintained map of which keys each write touches
 * goes stale the first time somebody adds a field.
 */
function useTempleMutation<TArgs, TData>(fn: (args: TArgs) => Promise<TData>) {
  const client = useQueryClient();
  return useMutation<TData, unknown, TArgs>({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.temples.all() }),
  });
}

export function useSetTempleStatus() {
  return useTempleMutation(
    ({ templeId, status, reason }: { templeId: number; status: string; reason?: string }) =>
      authedClient.request<TempleDetail>(`/v1/admin/temples/${templeId}/status`, {
        method: 'POST',
        body: { status, reason },
      }),
  );
}

export function useCreateAuthorization() {
  return useTempleMutation(
    ({
      templeId,
      body,
    }: {
      templeId: number;
      body: components['schemas']['AuthorizationCreateIn'];
    }) =>
      authedClient.request<AuthorizationRow>(`/v1/admin/temples/${templeId}/authorizations`, {
        method: 'POST',
        body,
      }),
  );
}

/** `submit` · `activate` · `suspend` · `resume` · `terminate`. Expiry is the sweep's, not ours. */
export type TransitionAction = 'submit' | 'activate' | 'suspend' | 'resume' | 'terminate';

export function useAuthorizationTransition() {
  return useTempleMutation(
    ({
      authorizationId,
      action,
      reason,
    }: {
      authorizationId: number;
      action: TransitionAction;
      reason?: string;
    }) =>
      authedClient.request<AuthorizationRow>(
        `/v1/admin/authorizations/${authorizationId}/${action}`,
        { method: 'POST', body: { reason } },
      ),
  );
}

type PresignResponse = components['schemas']['DocumentPresignResponse'];
type DocumentRow = components['schemas']['AuthorizationDocumentRow'];

/**
 * The two-phase confidential upload: presign → PUT the bytes → confirm.
 *
 * The PUT goes straight to storage with `fetch`, not through `authedClient` — the presigned URL
 * carries its own signature and adding our bearer token to it would be a second credential on a
 * request that does not want one.
 *
 * Confirm is what makes the row real: a presigned PUT cannot bound the body, so the declared size
 * is a claim and the server HEADs the object to get a fact. Activation refuses without at least
 * one `signed_agreement`, so this is the step that unblocks the whole lifecycle.
 */
type TemplePatchIn = components['schemas']['TemplePatchIn'];
type TempleCreateSchema = components['schemas']['TempleCreateIn'];
type TempleCreateRequired = 'city_id' | 'name' | 'latitude' | 'longitude';
/** The editorial fields are optional on create — they are written on the editor afterwards. */
export type TempleCreateIn = Pick<TempleCreateSchema, TempleCreateRequired> &
  Partial<Omit<TempleCreateSchema, TempleCreateRequired>>;
type DeityLinkIn = components['schemas']['DeityLinkIn'];

/** Always a draft: a temple page is never public the moment it is typed. */
export function useCreateTemple() {
  return useTempleMutation((body: TempleCreateIn) =>
    authedClient.request<TempleDetail>('/v1/admin/temples', { method: 'POST', body }),
  );
}

/** The deity set, replaced whole. The server refuses a set without exactly one primary. */
export function useReplaceTempleDeities() {
  return useTempleMutation(({ templeId, items }: { templeId: number; items: DeityLinkIn[] }) =>
    authedClient.request<TempleDetail>(`/v1/admin/temples/${templeId}/deities`, {
      method: 'PUT',
      body: { items },
    }),
  );
}
type TimingIn = components['schemas']['TimingIn'];
type FestivalIn = components['schemas']['FestivalIn'];

/**
 * The editorial fields — history, significance, architecture, how to reach, the SEO pair.
 *
 * ⛔ **Nothing here touches the official-wording claim.** That is derived from the agreements on
 * every read and appears and disappears on its own; an editor writing prose must not be able to
 * assert it in passing. `PATCH` carries no authorization field at all, which is what makes that
 * true rather than merely intended.
 */
export function usePatchTemple() {
  return useTempleMutation(({ templeId, body }: { templeId: number; body: TemplePatchIn }) =>
    authedClient.request<TempleDetail>(`/v1/admin/temples/${templeId}`, {
      method: 'PATCH',
      body,
    }),
  );
}

/**
 * The three replace-the-whole-list sets. One PUT per list, order is array position.
 *
 * Read and write shapes round-trip — `TimingRow` extends `TimingIn`, `FestivalRow` extends
 * `FestivalIn` — so an edit needs no translation and cannot lose a field the read never showed.
 */
export function useReplaceTimings() {
  return useTempleMutation(({ templeId, items }: { templeId: number; items: TimingIn[] }) =>
    authedClient.request<TempleDetail>(`/v1/admin/temples/${templeId}/timings`, {
      method: 'PUT',
      body: { items },
    }),
  );
}

export function useReplaceFestivals() {
  return useTempleMutation(({ templeId, items }: { templeId: number; items: FestivalIn[] }) =>
    authedClient.request<TempleDetail>(`/v1/admin/temples/${templeId}/festivals`, {
      method: 'PUT',
      body: { items },
    }),
  );
}

export function useUploadAgreementDocument() {
  return useTempleMutation(
    async ({
      authorizationId,
      file,
      kind,
      title,
    }: {
      authorizationId: number;
      file: File;
      kind: string;
      title: string;
    }) => {
      const presigned = await authedClient.request<PresignResponse>(
        `/v1/admin/authorizations/${authorizationId}/documents/presign`,
        {
          method: 'POST',
          body: { kind, content_type: file.type, file_size_bytes: file.size },
        },
      );
      await putPresigned(presigned.upload, file);
      return authedClient.request<DocumentRow>(
        `/v1/admin/authorizations/${authorizationId}/documents`,
        {
          method: 'POST',
          body: { storage_key: presigned.storage_key, kind, title },
        },
      );
    },
  );
}

export type TempleMediaRow = components['schemas']['TempleMediaRow'];
type MediaPresignResponse = components['schemas']['TempleMediaPresignResponse'];
type MediaPatchIn = components['schemas']['TempleMediaPatchIn'];

/** The kinds the server accepts. `hero` is the one the page opens with — at most one. */
export const MEDIA_KINDS = ['hero', 'photo', 'gallery', 'video'] as const;

/**
 * The public imagery: presign → PUT the bytes → confirm.
 *
 * Same shape as the signed-paper upload, with one difference that matters: presign already
 * writes the row, hidden. Confirm is the step that HEADs the object and only then shows it, so a
 * PUT that fails leaves a hidden row the operator can see and remove — never a broken image on a
 * public page.
 */
export function useAddTempleMedia() {
  return useTempleMutation(
    async ({
      templeId,
      file,
      kind,
      altText,
      caption,
    }: {
      templeId: number;
      file: File;
      kind: string;
      altText: string;
      caption?: string;
    }) => {
      const presigned = await authedClient.request<MediaPresignResponse>(
        `/v1/admin/temples/${templeId}/media`,
        {
          method: 'POST',
          body: {
            kind,
            content_type: file.type,
            file_size_bytes: file.size,
            alt_text: altText,
            caption: caption || null,
          },
        },
      );
      await putPresigned(presigned.upload, file, 'The image stays hidden — remove it and try again.');
      return authedClient.request<TempleMediaRow>(
        `/v1/admin/temples/media/${presigned.media.id}/confirm`,
        { method: 'POST' },
      );
    },
  );
}

/** Alt text, caption, order and visibility. Never the kind or the key — replace the image instead. */
export function usePatchTempleMedia() {
  return useTempleMutation(({ mediaId, body }: { mediaId: number; body: MediaPatchIn }) =>
    authedClient.request<TempleMediaRow>(`/v1/admin/temples/media/${mediaId}`, {
      method: 'PATCH',
      body,
    }),
  );
}

/**
 * Retire a temple page: soft-deleted and archived, with the reason on the audit row. The server
 * refuses while an agreement is active — the official claim would be left resting on nothing.
 */
export function useRetireTemple() {
  return useTempleMutation(({ templeId, reason }: { templeId: number; reason: string }) =>
    authedClient.request<void>(`/v1/admin/temples/${templeId}`, { method: 'DELETE', body: { reason } }),
  );
}

/**
 * Bring an archived temple back. It lands on draft, never straight to published, so the publish
 * gate runs again; the server refuses 409 slug_taken while a live temple holds the slug.
 */
export function useRestoreTemple() {
  return useTempleMutation(({ templeId, reason }: { templeId: number; reason: string }) =>
    authedClient.request<TempleDetail>(`/v1/admin/temples/${templeId}/restore`, {
      method: 'POST',
      body: { reason },
    }),
  );
}

export function useDeleteTempleMedia() {
  return useTempleMutation(({ mediaId }: { mediaId: number }) =>
    authedClient.request<void>(`/v1/admin/temples/media/${mediaId}`, { method: 'DELETE' }),
  );
}

type DownloadResponse = components['schemas']['DocumentDownloadResponse'];

/**
 * A link minted per click, not a cached one.
 *
 * Deliberately a mutation rather than a `useQuery`. The server writes an audit row before it
 * hands the URL over — "who read this agreement, and when" is a question the counterparty and a
 * regulator are both entitled to put to us — and a cached URL would let a second operator open
 * the paper under the first one's row. Caching would also outlive the link: it expires in
 * minutes, so a remembered one is a button that silently stops working.
 */
export function useDownloadAgreementDocument() {
  return useMutation<DownloadResponse, unknown, { authorizationId: number; documentId: number }>({
    mutationFn: ({ authorizationId, documentId }) =>
      authedClient.request<DownloadResponse>(
        `/v1/admin/authorizations/${authorizationId}/documents/${documentId}/download`,
      ),
  });
}

/* ── Research, linkage and verification (CSR-123; content 04 §8, doc 03 §9 and §13) ───────── */

export type RitualLinkIn = components['schemas']['RitualLinkIn'];
export type SourceIn = components['schemas']['SourceIn'];
export type ClosureIn = components['schemas']['ClosureIn'];
export type TempleVerificationState = components['schemas']['TempleVerificationState'];

/** The pujas this temple is a venue for. Gate T6 refuses to publish a temple with none. */
export function useReplaceTempleRituals() {
  return useTempleMutation(({ templeId, items }: { templeId: number; items: RitualLinkIn[] }) =>
    authedClient.request<TempleDetail>(`/v1/admin/temples/${templeId}/rituals`, { method: 'PUT', body: { items } }),
  );
}

/** Where each fact came from. Internal, never rendered; gate T4 refuses to publish with none. */
export function useReplaceTempleSources() {
  return useTempleMutation(({ templeId, items }: { templeId: number; items: SourceIn[] }) =>
    authedClient.request<TempleDetail>(`/v1/admin/temples/${templeId}/sources`, { method: 'PUT', body: { items } }),
  );
}

/** The seasons a temple closes and reopens (gate T7). */
export function useReplaceTempleClosures() {
  return useTempleMutation(({ templeId, items }: { templeId: number; items: ClosureIn[] }) =>
    authedClient.request<TempleDetail>(`/v1/admin/temples/${templeId}/closures`, { method: 'PUT', body: { items } }),
  );
}

export function useSetTempleVerification() {
  return useTempleMutation(
    ({ templeId, state, reason }: { templeId: number; state: TempleVerificationState; reason?: string }) =>
      authedClient.request<TempleDetail>(`/v1/admin/temples/${templeId}/verification`, {
        method: 'POST',
        body: { state, reason: reason || null },
      }),
  );
}
